<template>
    <div ref="scroller" class="bozp-viewer" @scroll="checkEnd">
        <p v-if="version.text" class="bozp-text">{{ version.text }}</p>
        <div v-if="version.file_name" ref="pages" class="bozp-pages"></div>
        <div v-if="loading" class="text-center text-muted py-3">{{ $t("bozp.loadingPdf") }}</div>
        <div v-if="error" class="alert alert-danger d-flex justify-content-between align-items-center">
            <span>{{ $t("bozp.pdfError") }}</span>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="render">{{ $t("bozp.retry") }}</button>
        </div>
    </div>
</template>

<script>
import workerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

export default {
    props: {
        version: { type: Object, required: true },
    },
    emits: ["reached-end"],
    data() {
        return {
            loading: false,
            error: false,
            reachedEnd: false,
            pageUrls: [],
        };
    },
    // renders once per mount; parents key the viewer by version id to show another version
    mounted() {
        this.render();
    },
    beforeUnmount() {
        this.releasePages();
    },
    methods: {
        releasePages() {
            this.pageUrls.forEach(url => URL.revokeObjectURL(url));
            this.pageUrls = [];
        },
        async render() {
            this.reachedEnd = false;
            this.error = false;

            if (this.version.file_name) {
                this.loading = true;
                this.$refs.pages.replaceChildren();
                this.releasePages();
                try {
                    // loaded on demand, pdf.js is too large for the main bundle; legacy build for older tablet Chrome
                    const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
                    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

                    const response = await this.$store.getters.api.get(
                        `/${this.$route.params.slug}/bozp/${this.version.id}/file`,
                        { responseType: "arraybuffer" }
                    );
                    const loadingTask = pdfjsLib.getDocument({ data: response.data });
                    const pdf = await loadingTask.promise;
                    const width = this.$refs.pages.clientWidth;
                    // sharper than 2x only costs memory a tablet doesn't have
                    const ratio = Math.min(window.devicePixelRatio || 1, 2);

                    for (let number = 1; number <= pdf.numPages; number++) {
                        const page = await pdf.getPage(number);
                        const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
                        const canvas = document.createElement("canvas");
                        canvas.width = Math.floor(viewport.width * ratio);
                        canvas.height = Math.floor(viewport.height * ratio);
                        await page.render({
                            canvas,
                            viewport,
                            transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null,
                        }).promise;

                        // an image instead of the canvas lets the browser drop pages scrolled out of view
                        const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
                        const image = new Image(canvas.width, canvas.height);
                        image.src = URL.createObjectURL(blob);
                        this.pageUrls.push(image.src);
                        await image.decode();
                        this.$refs.pages.appendChild(image);
                        canvas.width = 0;
                        canvas.height = 0;
                        page.cleanup();
                    }
                    // the document itself has no destroy() in pdf.js 6, its loading task frees the worker
                    await loadingTask.destroy();
                } catch (error) {
                    console.error("Error rendering BOZP PDF", error);
                    this.error = true;
                } finally {
                    this.loading = false;
                }
            }

            this.$nextTick(this.checkEnd);
        },
        checkEnd() {
            const el = this.$refs.scroller;
            if (this.reachedEnd || this.loading || this.error || !el) return;
            // small tolerance: fractional scroll positions never hit the exact bottom on some tablets
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
                this.reachedEnd = true;
                this.$emit("reached-end");
            }
        },
    },
};
</script>

<style scoped>
.bozp-viewer {
    height: 100%;
    overflow-y: auto;
}

.bozp-text {
    white-space: pre-wrap;
    font-size: 1.1rem;
}

.bozp-pages :deep(img) {
    display: block;
    width: 100%;
    height: auto;
    margin-bottom: 1rem;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
}
</style>
